import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import NearbyMap from "@/components/NearbyMap";
import { Loader2, Navigation } from "lucide-react";

export default function Nearby() {
  const [items, setItems] = useState([]);
  const [userPos, setUserPos] = useState(null); // { lat, lng, accuracy }
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const watchIdRef = useRef(null);

  // Stop watching GPS on unmount
  useEffect(() => () => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
  }, []);

  const fetchNearby = async (lat, lng) => {
    try {
      const { data } = await api.get("/profiles/nearby", {
        params: { lat, lng, limit: 60 },
      });
      setItems(data || []);
      if (!data?.length) {
        setError("Aún no hay musas con ubicación en tu zona. Prueba más tarde.");
      } else {
        setError("");
      }
    } catch (e) {
      setError(e.message || "Error al buscar.");
    }
  };

  const activate = () => {
    if (!navigator.geolocation) {
      setError("Tu navegador no soporta geolocalización.");
      return;
    }
    setLoading(true);
    setError("");
    // One-shot first to load data fast, then start watching for updates
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        setUserPos({ lat: coords.latitude, lng: coords.longitude, accuracy: coords.accuracy });
        setStarted(true);
        await fetchNearby(coords.latitude, coords.longitude);
        setLoading(false);

        // Then watch for live movement
        watchIdRef.current = navigator.geolocation.watchPosition(
          ({ coords: c }) => setUserPos({ lat: c.latitude, lng: c.longitude, accuracy: c.accuracy }),
          () => {},
          { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
        );
      },
      (err) => {
        setError("No pudimos obtener tu ubicación: " + err.message);
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="w-full" data-testid="nearby-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-4">
        <div className="overline mb-2">Cerca de ti</div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <h1 className="font-serif text-4xl md:text-5xl font-light" style={{ color: "var(--text-primary)" }}>
            Musas en tu <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>radio</em>
          </h1>
          {started && (
            <p className="text-xs" style={{ color: "var(--text-secondary)" }} data-testid="nearby-count">
              {items.length} {items.length === 1 ? "musa encontrada" : "musas encontradas"} cerca
            </p>
          )}
        </div>
        {!started && (
          <p className="text-sm max-w-2xl mt-3" style={{ color: "var(--text-secondary)" }}>
            Activa tu ubicación para descubrir en el mapa las musas más cercanas a ti en tiempo real.
            Tu ubicación nunca sale de este navegador.
          </p>
        )}
      </div>

      {!started && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-8">
          <button
            className="btn-primary inline-flex items-center gap-2"
            disabled={loading}
            onClick={activate}
            data-testid="nearby-geo-btn"
          >
            {loading ? (
              <><Loader2 size={14} className="animate-spin" /> Obteniendo ubicación…</>
            ) : (
              <><Navigation size={14} /> Usar mi ubicación</>
            )}
          </button>
          {error && (
            <p className="text-sm mt-4" style={{ color: "#ef6d6d" }} data-testid="nearby-error">
              {error}
            </p>
          )}
        </div>
      )}

      {started && (
        <div className="px-0 md:px-6 pb-8" data-testid="nearby-map-container">
          <NearbyMap profiles={items} userPos={userPos} />
          {error && (
            <p className="text-sm mt-4 px-6" style={{ color: "#ef6d6d" }} data-testid="nearby-error">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
